import {
  TableHead,
  TableRow,
  TableCell,
  TableSortLabel,
  TableBody,
} from "@mui/material";
import { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../../types/types";
import { AssistantValue } from "../../../../../../../services/api/instances/assistantsApi/types";
import { useAppSelector } from "../../../../../../../services/redux/hooks/use-selector";
import {
  TABLE_HEADER_EN,
  TABLE_HEADER_HY,
  TABLE_HEADER_RU,
} from "../../../../../constant";
import { selectedLangSelector } from "../../../../../../../services/i18n/redux/selectors";
import { AppTable } from "../../../../../../../shared/components/AppTable";
import { AppButton } from "../../../../../../../shared/components/AppButton";
import { TModalsIds } from "../../../../../../../app/state/modalSlice/types";
import { TEntityInfo } from "../../../../../../../common/types/utility";
import { ChatObject } from "../../../../../../../services/api/instances/botApi/types";
import styles from "./styles.module.css";
import { NormalizedStorageObject } from "../../../../../../../services/api/instances/storageApi/types/object_types";

interface Props {
  chats: ChatObject[];
  assistants: NormalizedStorageObject<AssistantValue>[];
  handleOpenChangeChatAssistantModal: (
    id: TModalsIds,
    info: TEntityInfo | null,
  ) => void;
  className?: string;
}

export const ChatbotTable = ({
  chats,
  assistants,
  handleOpenChangeChatAssistantModal,
  className,
}: Props) => {
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof ChatObject>("chat_name");
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const { t } = useTranslation();
  const selectedLang = useAppSelector(selectedLangSelector);

  const getTableLang = () => {
    switch (selectedLang) {
      case "en":
        return TABLE_HEADER_EN;
      case "hy":
        return TABLE_HEADER_HY;
      case "ru":
        return TABLE_HEADER_RU;
      default:
        return TABLE_HEADER_RU;
    }
  };

  const handleRequestSort = (property: keyof ChatObject) => (event: any) => {
    event.preventDefault();
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const SORTED_ROWS = chats
    ? [...chats].sort((a: ChatObject, b: ChatObject) => {
        if (a[orderBy] < b[orderBy]) {
          return order === "asc" ? -1 : 1;
        }
        if (a[orderBy] > b[orderBy]) {
          return order === "asc" ? 1 : -1;
        }
        return 0;
      })
    : [];

  const getLocalAssistantName = (assistantId: string): string => {
    let assistantName = "";
    let localAssistant: NormalizedStorageObject<AssistantValue> | undefined;

    localAssistant = assistants?.find(
      (assistant) => assistant?.value?.assistant_object?.id === assistantId,
    );
    if (assistantId === "def") {
      return "";
    }
    if (localAssistant) {
      assistantName = localAssistant.friendly_name;
    }
    return assistantName;
  };

  return (
    <AppTable className={className}>
      <TableHead>
        <TableRow>
          {getTableLang().map((data) => (
            <TableCell
              key={data.id}
              className={classNames({ [styles["header-small"]]: data.small })}
            >
              <TableSortLabel
                active={orderBy === data.id}
                direction={orderBy === data.id ? order : "asc"}
                onClick={handleRequestSort(data.id)}
              >
                {data.label}
              </TableSortLabel>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {SORTED_ROWS?.map((row) => (
          <TableRow
            key={row.chat_id}
            onMouseEnter={() => setHoveredRowId(row.chat_id)}
            onMouseLeave={() => setHoveredRowId(null)}
            className={classNames(styles.row, {
              [styles["hovered-row"]]: hoveredRowId === row.chat_id,
            })}
          >
            <TableCell className={styles.small}>{row.chat_id}</TableCell>
            <TableCell>{row.chat_name}</TableCell>
            <TableCell className={styles.assistant}>
              {getLocalAssistantName(row.asst_name)}

              <AppButton
                small
                buttonType="text"
                className={classNames(styles["change-button"], {
                  [styles.hidden]: hoveredRowId !== row.chat_id,
                })}
                onClick={() =>
                  handleOpenChangeChatAssistantModal(
                    "change_chat_assistant_modal",
                    { id: row.chat_id, value: row.asst_name },
                  )
                }
              >
                {t("chatbot.change_bot_button")}
              </AppButton>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </AppTable>
  );
};
